/**
 * ============================================================================
 * MetricDrillDownDrawer — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: slide-in panel anchored to the inline-end edge. Lists the entities
 *          that drive a metric (deviations behind OPEN_DEVIATIONS, docs behind
 *          DOC_OVERDUE_PCT, …). The drawer is its own focus trap so the user
 *          can navigate the list with Tab/Shift+Tab without "escaping" back
 *          into the dashboard.
 *
 * A11Y:
 *   - role="dialog" + aria-modal so AT treats it as a modal surface.
 *   - Initial focus → close button; Escape closes; focus restoration is the
 *     parent's responsibility via onClose (we don't manage the trigger ref
 *     ourselves).
 * ============================================================================ */

import { useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { useMetricDrillDownQuery } from './useMetricDrillDownQuery';
import type { QualityMetric } from './useDashboardQuery';
import styles from './QualityMetricsDashboard.module.css';

export interface MetricDrillDownDrawerProps {
  metric: QualityMetric;
  onClose: () => void;
}

export function MetricDrillDownDrawer({ metric, onClose }: MetricDrillDownDrawerProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const drillQ = useMetricDrillDownQuery(metric.metric_key);

  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  // Trap focus inside the drawer until it closes; close on Escape.
  useEffect(() => {
    closeBtnRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = drillQ.data?.items ?? [];
  const metricLabel = t(`qualityMetrics.metric.${metric.metric_key}.label`, {
    defaultValue: metric.label,
  });

  return (
    <div
      className={styles.drawerBackdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={styles.drawerHeader}>
          <div>
            <h2 id={titleId} className={styles.drawerTitle}>
              {t('qualityMetrics.drawer.title', {
                defaultValue: 'Drill-down: {{metric}}',
                metric: metricLabel,
              })}
            </h2>
            <p id={descId} className={styles.drawerSubtitle}>
              {t('qualityMetrics.drawer.subtitle', {
                defaultValue:
                  'Entities contributing to this metric. Click a row to open the source record.',
              })}
            </p>
          </div>
          <Button
            ref={closeBtnRef}
            variant="tertiary"
            size="sm"
            onClick={onClose}
            aria-label={t('qualityMetrics.drawer.closeAria', {
              defaultValue: 'Close drill-down drawer',
            })}
          >
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </header>

        <div className={styles.drawerBody}>
          {drillQ.isLoading && (
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.drawerSkeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {drillQ.isError && (
            <div role="alert" className={styles.drawerError}>
              <span>
                {t('qualityMetrics.drawer.loadError', {
                  defaultValue: 'Could not load drill-down for this metric.',
                })}
              </span>
              <Button variant="secondary" size="sm" onClick={() => drillQ.refetch()}>
                {t('qualityMetrics.actions.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {!drillQ.isLoading && !drillQ.isError && items.length === 0 && (
            <p className={styles.drawerEmpty}>
              {t('qualityMetrics.drawer.empty', {
                defaultValue: 'No contributing entities. The metric is clean.',
              })}
            </p>
          )}

          {items.length > 0 && (
            <ul
              className={styles.drawerList}
              aria-label={t('qualityMetrics.drawer.listAria', {
                defaultValue: 'Contributing entities',
              })}
            >
              {items.map((row) => (
                <li key={row.entity_id} className={styles.drawerListItem}>
                  <div className={styles.drawerRowMain}>
                    <span className={styles.drawerRowKind} aria-hidden="true">
                      {row.entity_kind}
                    </span>
                    <span className={styles.drawerRowTitle}>{row.title}</span>
                    {row.subtitle && (
                      <span className={styles.drawerRowSubtitle}>{row.subtitle}</span>
                    )}
                  </div>
                  <span
                    className={styles.drawerRowContribution}
                    aria-label={t('qualityMetrics.drawer.contributionAria', {
                      defaultValue: 'Contribution: {{value}}',
                      value: nf.format(row.contribution),
                    })}
                  >
                    {nf.format(row.contribution)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className={styles.drawerFooter}>
          <span className={styles.drawerFooterMeta}>
            {t('qualityMetrics.drawer.footerMeta', {
              defaultValue: '{{total}} contributing entities · generated {{ts}}',
              total: nf.format(drillQ.data?.total ?? items.length),
              ts: drillQ.data?.generated_at_utc
                ? new Date(drillQ.data.generated_at_utc).toLocaleString(i18n.language)
                : '—',
            })}
          </span>
        </footer>
      </aside>
    </div>
  );
}
